package com.sweetlime.bingeamigo.presentation.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.bingeamigo.model.NextEpisodeCardState
import com.sweetlime.bingeamigo.presentation.ui.AccentRoles
import com.sweetlime.bingeamigo.presentation.ui.BrandColors
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

@Composable
fun ContinueWatchingRail(
    cards: List<NextEpisodeCardState>,
    featuredShowId: Int,
    accent: AccentRoles,
    isNewEpisode: (NextEpisodeCardState) -> Boolean,
    onSelect: (NextEpisodeCardState) -> Unit,
    onMarkWatched: (NextEpisodeCardState) -> Unit,
) {
    LazyRow(
        horizontalArrangement = Arrangement.spacedBy(11.dp),
        contentPadding = PaddingValues(horizontal = 16.dp),
    ) {
        items(cards, key = { it.tvShow.id }) { card ->
            ContinueWatchingCard(
                card = card,
                accent = accent,
                active = card.tvShow.id == featuredShowId,
                isNew = isNewEpisode(card),
                onSelect = { onSelect(card) },
                onMark = { onMarkWatched(card) },
            )
        }
    }
}

@Composable
private fun ContinueWatchingCard(
    card: NextEpisodeCardState,
    accent: AccentRoles,
    active: Boolean,
    isNew: Boolean,
    onSelect: () -> Unit,
    onMark: () -> Unit,
) {
    val backdropUrl = "https://image.tmdb.org/t/p/w500${card.tvShow.backdropPath}"
    val progress = if (card.totalThisSeason > 0)
        card.watchedThisSeason.toFloat() / card.totalThisSeason.toFloat() else 0f
    val done = card.watchedThisSeason >= card.totalThisSeason
    val season = "S${card.seasonNumber.toString().padStart(2, '0')}"
    val episode = "E${card.episodeNumber.toString().padStart(2, '0')}"

    @OptIn(ExperimentalTime::class)
    val todayStr = remember {
        val ms = Clock.System.now().toEpochMilliseconds()
        val days = (ms / 86_400_000L).toInt()
        var n = days + 719468
        val era = if (n >= 0) n else n - 146096
        val doe = era % 146097
        val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
        val y = yoe + (era / 146097) * 400
        val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
        val mp = (5 * doy + 2) / 153
        val d = doy - (153 * mp + 2) / 5 + 1
        val m = if (mp < 10) mp + 3 else mp - 9
        val year = if (m <= 2) y + 1 else y
        "${year}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
    }
    val isFuture = remember(card.airDate) { card.airDate.length == 10 && card.airDate > todayStr }

    Column(
        modifier = Modifier
            .width(156.dp)
            .clip(RoundedCornerShape(14.dp))
            .background(BrandColors.Card)
            .border(
                width = 1.5.dp,
                color = if (active) accent.ring else Color(0xFF2A2A2E),
                shape = RoundedCornerShape(14.dp),
            )
            .clickable { onSelect() },
    ) {
        Box(Modifier.fillMaxWidth().height(90.dp)) {
            KamelImage(
                resource = asyncPainterResource(backdropUrl),
                contentDescription = card.tvShow.name,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize(),
            )
            Box(
                Modifier.fillMaxSize().background(
                    Brush.verticalGradient(0.4f to Color.Transparent, 1f to Color(0xB3141416))
                )
            )
            if (isNew) {
                Surface(color = BrandColors.Primary, shape = RoundedCornerShape(5.dp), modifier = Modifier.align(Alignment.TopStart).padding(7.dp)) {
                    Text("NEW", color = Color(0xFF1A1407), fontWeight = FontWeight.ExtraBold, fontSize = 9.5.sp, letterSpacing = 0.4.sp, modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.5.dp))
                }
            }
            Surface(
                onClick = onMark,
                enabled = !done && !isFuture,
                shape = CircleShape,
                color = if (done || isFuture) Color.White.copy(alpha = 0.22f) else accent.action,
                modifier = Modifier.align(Alignment.BottomEnd).padding(7.dp).size(28.dp),
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(Icons.Filled.Check, "Mark next watched", tint = if (done || isFuture) Color.White.copy(alpha = 0.4f) else accent.actionInk, modifier = Modifier.size(15.dp))
                }
            }
        }
        Column(Modifier.padding(start = 10.dp, end = 10.dp, top = 8.dp, bottom = 11.dp)) {
            Text(card.tvShow.name, color = Color.White, fontWeight = FontWeight.Bold, fontSize = 13.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Spacer(Modifier.height(2.dp))
            Text(if (done) "Caught up" else "$season$episode next", color = BrandColors.TextSecondary, fontSize = 11.5.sp)
            Spacer(Modifier.height(8.dp))
            LinearProgressIndicator(
                progress = { progress },
                modifier = Modifier.fillMaxWidth().height(3.5.dp).clip(RoundedCornerShape(3.dp)),
                color = accent.progress,
                trackColor = Color.White.copy(alpha = 0.13f),
            )
        }
    }
}
